import { useEffect, useState } from 'react'

const update_url = import.meta.env.VITE_CLIENT_UPDATE_URL || 'https://metamory.cc/download/client/update.json'

interface UpdateJson {
  version: string
  platforms: {
    [key: string]: {
      url: string
      signature: string
    }
  }
}

type UpdateResult = { download_url: string | null; version: string | null }
type UpdateState = UpdateResult & { is_loading: boolean }

// Module-level cache to avoid duplicate fetches
let cached: UpdateResult | null = null
let fetching: Promise<UpdateResult> | null = null

export const resetUseClientDownloadUrlCacheForTest = () => {
  cached = null
  fetching = null
}

const fetchUpdate = (): Promise<UpdateResult> => {
  if (cached) return Promise.resolve(cached)
  if (fetching) return fetching
  if (!update_url) return Promise.resolve({ download_url: null, version: null })

  fetching = fetch(update_url)
    .then(res => res.json())
    .then((data: UpdateJson) => {
      cached = {
        download_url: data.platforms?.['windows-x86_64']?.url ?? null,
        version: data.version ?? null,
      }
      return cached
    })
    .catch(() => {
      cached = { download_url: null, version: null }
      return cached
    })
    .finally(() => {
      fetching = null
    })
  return fetching
}

export const useClientDownloadUrl = () => {
  const [state, setState] = useState<UpdateState>(() =>
    cached
      ? { ...cached, is_loading: false }
      : { download_url: null, version: null, is_loading: true },
  )

  useEffect(() => {
    let cancelled = false

    fetchUpdate().then(result => {
      if (cancelled) return
      setState({ ...result, is_loading: false })
    })

    return () => {
      cancelled = true
    }
  }, [])

  return state
}
